import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { Link } from 'react-router-dom';

import { policyName } from '@/features/decisions/config/personaContext';
import type { PageInfo, ResolvedReplenishmentContext } from '@/features/decisions/decisions.api.types';
import {
  classifyDelta,
  DELTA_STYLES,
  fmtMoneyK,
  fmtPercent,
  fmtPlanningHorizon,
  fmtUnitsK,
  type GroupedRow,
} from '@/features/decisions/utils/queueFormat';
import { productStockingLocationHref, type PersonaLinkContext } from '@/features/decisions/utils/replenishmentLinks';

interface Props {
  groups: GroupedRow[];
  resolved: ResolvedReplenishmentContext | undefined;
  pageInfo: PageInfo | undefined;
  primaryCompareId: string;
  period: string;
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  isLoading: boolean;
  error: Error | null;
  elapsedMs: number | undefined;
  // Carried into the SKU × Site detail link.
  persona: PersonaLinkContext;
}

export function ReplenishmentQueueTable({
  groups,
  resolved,
  pageInfo,
  primaryCompareId,
  period,
  page,
  setPage,
  pageSize,
  isLoading,
  error,
  elapsedMs,
  persona,
}: Props) {
  if (isLoading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white px-4 py-6 text-sm text-gray-600">
        Loading replenishment recommendations…
      </div>
    );
  }
  if (error) {
    const isNoData = error.message.includes('no data');
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        {isNoData ? (
          'No replenishment data found for the selected date and scenario. Try adjusting the period or scenario settings.'
        ) : (
          <>
            <span className="font-medium">Query failed:</span> {error.message}
          </>
        )}
      </div>
    );
  }

  const totalGroups = pageInfo?.total_groups ?? 0;
  if (!resolved || totalGroups === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white px-4 py-6 text-sm text-gray-500">
        No rows for this experiment pair at period <span className="font-mono">{period}</span>.
      </div>
    );
  }

  const effectivePageSize = pageInfo?.page_size ?? pageSize;
  const totalPages = Math.max(1, Math.ceil(totalGroups / effectivePageSize));
  const startIdx = (page - 1) * effectivePageSize + 1;
  const endIdx = Math.min(totalGroups, startIdx + groups.length - 1);

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white" data-testid="rep-queue-table">
      <div className="px-4 py-3">
        <div className="text-sm font-semibold text-gray-900">
          Underage / Overage Queue{' '}
          <span className="font-normal text-gray-500">
            ({startIdx}–{endIdx} of {totalGroups} SKU × Sites)
          </span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-y border-gray-200 bg-white text-xs text-gray-500">
              <th className="px-4 py-2 text-left font-medium" title="row.sku · row.site">
                SKU × Site
              </th>
              <th className="px-4 py-2 text-left font-medium" title="inventory_strategy">
                Stocking Strategy
              </th>
              <th className="px-4 py-2 text-left font-medium" title="resolved.experiment_ids">
                Planning Policy
              </th>
              <th className="px-4 py-2 text-right font-medium" title="model_beginning_available × cost_per_unit">
                Available Inventory On-Hand
              </th>
              <th className="px-4 py-2 text-right font-medium" title="model_target_service_level">
                Target Service Level
              </th>
              <th className="px-4 py-2 text-right font-medium" title="total_lead_time + periods_between_replenishment">
                Planning Horizon (LT + PBR)
              </th>
              <th className="px-4 py-2 text-right font-medium" title="model_target_inv_pos · model_order_constrained">
                Incumbent Recommendation
                <div className="text-[10px] font-normal text-gray-400">
                  {policyName(resolved.ref_experiment_id, true)}
                </div>
                <div className="text-[10px] font-normal text-gray-400">Reorder Point · Reorder Qty</div>
              </th>
              <th
                className="bg-[#274483]/5 px-4 py-2 text-right font-medium"
                title="model_target_inv_pos · model_order_constrained"
              >
                Comparison Recommendation
                <div className="text-[10px] font-normal text-gray-400">
                  {primaryCompareId ? policyName(primaryCompareId) : '—'}
                </div>
                <div className="text-[10px] font-normal text-gray-400">Reorder Point · Reorder Qty</div>
              </th>
              <th className="px-4 py-2 text-right font-medium" title="derived: Δ(model_order_constrained)">
                Delta to Plan
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {groups.map((g) => {
              const r = g.ref ?? g.cmp;
              const strategy = r?.inventory_strategy ?? '';
              const delta = classifyDelta(g.ref?.model_order_constrained, g.cmp?.model_order_constrained);
              const ds = DELTA_STYLES[delta];
              return (
                <tr key={`${g.sku}-${g.site}`} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap px-4 py-2.5">
                    <Link
                      to={productStockingLocationHref({ sku: g.sku, site: g.site }, persona)}
                      className="font-medium text-blue-700 hover:text-blue-900 hover:underline"
                    >
                      {g.sku}
                    </Link>
                    <span className="text-gray-400"> · </span>
                    <span className="text-gray-700">{g.site}</span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5">
                    {strategy ? (
                      <span className="rounded border border-gray-200 bg-white px-2 py-0.5 text-xs font-medium text-gray-700">
                        {strategy}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5">
                    {g.cmp?.experiment_id ? (
                      <span className="rounded border border-[#274483]/30 bg-[#274483]/10 px-2 py-0.5 text-[11px] font-medium text-[#274483]">
                        {policyName(g.cmp.experiment_id)}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td
                    className="whitespace-nowrap px-4 py-2.5 text-right text-gray-800"
                    title={
                      r?.model_beginning_available != null
                        ? `${r.model_beginning_available.toLocaleString()} units`
                        : undefined
                    }
                  >
                    {fmtMoneyK(r?.model_beginning_available ?? null, r?.cost_per_unit ?? null)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-right text-gray-800">
                    {fmtPercent(r?.model_target_service_level ?? null)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-right text-gray-800">
                    {fmtPlanningHorizon(r?.total_lead_time ?? null, r?.periods_between_replenishment ?? null)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-right">
                    <div className="font-semibold tabular-nums text-gray-900">
                      {fmtUnitsK(g.ref?.model_target_inv_pos ?? null)}{' '}
                      <span className="text-[10px] font-normal text-gray-500">ROP</span>
                    </div>
                    <div className="text-xs tabular-nums text-gray-600">
                      {fmtUnitsK(g.ref?.model_order_constrained ?? null)}{' '}
                      <span className="text-[10px] text-gray-400">Qty</span>
                    </div>
                  </td>
                  <td className="whitespace-nowrap bg-[#274483]/5 px-4 py-2.5 text-right">
                    <div className="font-semibold tabular-nums text-[#1E2F52]">
                      {fmtUnitsK(g.cmp?.model_target_inv_pos ?? null)}{' '}
                      <span className="text-[10px] font-normal text-[#274483]">ROP</span>
                    </div>
                    <div className="text-xs tabular-nums text-[#274483]">
                      {fmtUnitsK(g.cmp?.model_order_constrained ?? null)}{' '}
                      <span className="text-[10px] text-[#274483]">Qty</span>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-right">
                    <span
                      className={`inline-block rounded border px-2 py-0.5 text-[10px] font-semibold tracking-wide ${ds.bg} ${ds.fg} ${ds.border}`}
                    >
                      {ds.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Right padding clears the floating KAI assistant button (fixed bottom-right, ~76px footprint). */}
      <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50 py-2 pl-4 pr-20 text-xs text-gray-500">
        <span>
          Showing{' '}
          <span className="font-medium text-gray-700">
            {startIdx}–{endIdx}
          </span>{' '}
          of <span className="font-medium text-gray-700">{totalGroups}</span> · page {page} of {totalPages}
          {elapsedMs != null && (
            <>
              {' '}
              ·{' '}
              <span className="font-mono text-gray-600" title="Client-side roundtrip (fetch + parse).">
                {elapsedMs} ms
              </span>
            </>
          )}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page <= 1}
            aria-label="Previous page"
            className="rounded border border-gray-300 bg-white p-0.5 text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-300"
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setPage(page + 1)}
            disabled={page >= totalPages}
            aria-label="Next page"
            className="rounded border border-gray-300 bg-white p-0.5 text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-300"
          >
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
